import React, { useState, useMemo } from "react";
import { Calendar, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getDaysInMonth, getISODay, startOfMonth, addMonths } from "date-fns";

export default function DateTimeSelector({ isOpen, onClose, onSelect }) {
  const { t } = useTranslation('datetime');
   const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const getTomorrow = () => {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    return d;
  };

  const isSameDay = (a, b) =>
    a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("20:00");

  const timeOptions = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, "0")}:00`);

  const monthNames = [
    t('months.january'), t('months.february'), t('months.march'), t('months.april'),
    t('months.may'), t('months.june'), t('months.july'), t('months.august'),
    t('months.september'), t('months.october'), t('months.november'), t('months.december'),
  ].map(m => m.toUpperCase());

  const dayAbbreviations = [
    t('days.mon_letter'), t('days.tue_letter'), t('days.wed_letter'), t('days.thu_letter'),
    t('days.fri_letter'), t('days.sat_letter'), t('days.sun_letter'),
  ];

  const viewDate = new Date(currentYear, currentMonth, 1);
  const daysInMonth = getDaysInMonth(viewDate);
  // getISODay returns 1 (Mon) – 7 (Sun); offset for Mon-start grid = getISODay - 1
  const adjustedFirstDay = getISODay(startOfMonth(viewDate)) - 1;

  const calendarDays = [];
  for (let i = 0; i < adjustedFirstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);

  const getHeaderText = (date = selectedCalendarDate, start = startTime, end = endTime) => {
    if (!date) return t("pick_day");
    const day = date.getDate();
    const month = monthNames[date.getMonth()].toLowerCase().slice(0, 3);
    return `${day} ${month} ${t("from")} ${start} ${t("to")} ${end}`;
  };

  const handleQuickSelect = (type) => {
    setSelectedDate(type);
    const date = type === "today" ? today : getTomorrow();
    setSelectedCalendarDate(date);
    setCurrentMonth(date.getMonth());
    setCurrentYear(date.getFullYear());
  };

  const handleCalendarPick = (dateObj) => {
    setSelectedCalendarDate(dateObj);
    setSelectedDate("other");
  };

  const handleDone = () => {
    onSelect?.(getHeaderText());
    onClose?.();
  };

  const handleClear = () => {
    setSelectedDate(null);
    setSelectedCalendarDate(null);
    setStartTime("08:00");
    setEndTime("20:00");
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };

  const canNavigateToPrevMonth = () =>
    !(currentMonth === today.getMonth() && currentYear === today.getFullYear());

  const handlePrevMonth = () => {
    if (!canNavigateToPrevMonth()) return;
    const prev = addMonths(new Date(currentYear, currentMonth, 1), -1);
    setCurrentMonth(prev.getMonth());
    setCurrentYear(prev.getFullYear());
  };

  const handleNextMonth = () => {
    const next = addMonths(new Date(currentYear, currentMonth, 1), 1);
    setCurrentMonth(next.getMonth());
    setCurrentYear(next.getFullYear());
  };

  if (!isOpen) return null;

  const quickOptions = [
    { id: "today",    label: t("today"),    accent: "#6C8EEF", bg: "rgba(108,142,239,0.12)" },
    { id: "tomorrow", label: t("tomorrow"), accent: "#59C3E8", bg: "rgba(89,195,232,0.12)"  },
    { id: "other",    label: t("other_day"), accent: "#8B7FC7", bg: "rgba(139,127,199,0.12)" },
  ];

  return (
    <div
      className="absolute top-full left-0 mt-2 z-50 w-80 rounded-2xl overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.88)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        boxShadow: "0 16px 48px rgba(100,100,160,0.14)",
        border: "1px solid rgba(255,255,255,0.90)",
      }}
      onClick={e => e.stopPropagation()}
    >
      <div className="p-5">
        {/* Quick select */}
        <p style={{ fontSize: "10px", fontWeight: 700, color: "#8A8AAA", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: "12px" }}>
          {t('pick_day')}
        </p>
        <div className="grid grid-cols-3 gap-2 mb-5">
          {quickOptions.map((d) => (
            <button
              key={d.id}
              onClick={() => d.id === "other" ? setSelectedDate("other") : handleQuickSelect(d.id)}
              className="py-2.5 rounded-xl text-sm font-semibold transition-all duration-150"
              style={
                selectedDate === d.id
                  ? { background: d.bg, color: d.accent, border: `1.5px solid ${d.accent}40` }
                  : { background: "rgba(232,236,247,0.70)", color: "#5A5A7A", border: "1.5px solid transparent" }
              }
              onMouseEnter={e => {
                if (selectedDate !== d.id) { e.currentTarget.style.background = d.bg; e.currentTarget.style.color = d.accent; }
              }}
              onMouseLeave={e => {
                if (selectedDate !== d.id) { e.currentTarget.style.background = "rgba(232,236,247,0.70)"; e.currentTarget.style.color = "#5A5A7A"; }
              }}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Inline calendar for "Друг ден" */}
        {selectedDate === "other" && (
          <>
            <div className="h-px mb-4" style={{ background: "rgba(108,142,239,0.15)" }} />
            <div className="flex justify-between items-center mb-4">
              <button
                onClick={handlePrevMonth}
                disabled={!canNavigateToPrevMonth()}
                className="w-8 h-8 rounded-xl flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition"
                style={{ background: "rgba(108,142,239,0.10)" }}
                onMouseEnter={e => canNavigateToPrevMonth() && (e.currentTarget.style.background = "rgba(108,142,239,0.20)")}
                onMouseLeave={e => (e.currentTarget.style.background = "rgba(108,142,239,0.10)")}
              >
                <ChevronLeft className="w-4 h-4" style={{ color: "#6C8EEF" }} />
              </button>
              <span style={{ fontSize: "11px", fontWeight: 700, color: "#2D2D4E", letterSpacing: "0.10em" }}>
                {monthNames[currentMonth]} {currentYear}
              </span>
              <button
                onClick={handleNextMonth}
                className="w-8 h-8 rounded-xl flex items-center justify-center transition"
                style={{ background: "rgba(108,142,239,0.10)" }}
                onMouseEnter={e => (e.currentTarget.style.background = "rgba(108,142,239,0.20)")}
                onMouseLeave={e => (e.currentTarget.style.background = "rgba(108,142,239,0.10)")}
              >
                <ChevronRight className="w-4 h-4" style={{ color: "#6C8EEF" }} />
              </button>
            </div>

            <div className="grid grid-cols-7 mb-2">
              {dayAbbreviations.map((d, i) => (
                <div key={i} style={{ textAlign: "center", fontSize: "10px", fontWeight: 700, color: "#8A8AAA", padding: "4px 0", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
              {calendarDays.map((day, i) => {
                if (!day) return <div key={i} />;
                const dateObj = new Date(currentYear, currentMonth, day);
                const isPast = dateObj < today;
                const isSelected = isSameDay(dateObj, selectedCalendarDate);
                const isToday = isSameDay(dateObj, today);

                return (
                  <button
                    key={i}
                    disabled={isPast}
                    onClick={() => !isPast && handleCalendarPick(dateObj)}
                    className="h-8 w-full rounded-xl text-sm font-medium transition-all duration-100"
                    style={
                      isPast
                        ? { color: "rgba(138,138,170,0.30)", cursor: "not-allowed" }
                        : isSelected
                        ? { background: "linear-gradient(135deg, #8B7FC7, #6C8EEF)", color: "#fff", fontWeight: 700, boxShadow: "0 4px 12px rgba(108,142,239,0.30)" }
                        : isToday
                        ? { background: "rgba(108,142,239,0.10)", color: "#6C8EEF", outline: "2px solid rgba(108,142,239,0.30)", fontWeight: 700 }
                        : { color: "#2D2D4E" }
                    }
                    onMouseEnter={e => { if (!isPast && !isSelected) e.currentTarget.style.background = "rgba(108,142,239,0.10)"; }}
                    onMouseLeave={e => {
                      if (!isPast && !isSelected && !isToday) e.currentTarget.style.background = "";
                      else if (isToday && !isSelected) e.currentTarget.style.background = "rgba(108,142,239,0.10)";
                    }}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Time range */}
        {selectedDate && (selectedDate !== "other" || selectedCalendarDate) && (
          <>
            <div className="h-px mb-4" style={{ background: "rgba(108,142,239,0.15)" }} />
            <p style={{ fontSize: "10px", fontWeight: 700, color: "#8A8AAA", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: "12px" }}>
              {t('pick_time')}
            </p>
            <div className="flex gap-3 items-center">
              {[
                { label: t('from'), value: startTime, setter: setStartTime, accent: "#6C8EEF" },
                { label: t('to'),   value: endTime,   setter: setEndTime,   accent: "#8B7FC7" },
              ].map(({ label, value, setter, accent }) => (
                <div key={label} className="flex-1">
                  <p style={{ fontSize: "11px", color: "#8A8AAA", marginBottom: "4px" }}>{label}</p>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: accent }} />
                    <select
                      value={value}
                      onChange={e => setter(e.target.value)}
                      className="w-full pl-8 pr-2 py-2.5 rounded-xl text-sm outline-none transition-all"
                      style={{ background: "rgba(232,236,247,0.70)", border: "1.5px solid transparent", color: "#2D2D4E" }}
                      onFocus={e => { e.currentTarget.style.border = `1.5px solid ${accent}60`; e.currentTarget.style.background = "#fff"; }}
                      onBlur={e => { e.currentTarget.style.border = "1.5px solid transparent"; e.currentTarget.style.background = "rgba(232,236,247,0.70)"; }}
                    >
                      {timeOptions.map(opt => <option key={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div
        className="flex gap-2 p-4"
        style={{ background: "rgba(232,236,247,0.80)", borderTop: "1px solid rgba(108,142,239,0.12)" }}
      >
        <button
          onClick={handleClear}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition"
          style={{
            color: "#5A5A7A",
            background: "rgba(255,255,255,0.75)",
            border: "1px solid rgba(108,142,239,0.15)",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.95)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.75)"}
        >
          {t('clear')}
        </button>
        <button
          onClick={handleDone}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200"
          style={{
            background: "linear-gradient(135deg, #8B7FC7, #6C8EEF)",
            boxShadow: "0 4px 16px rgba(108,142,239,0.32)",
          }}
        >
          {t('done')}
        </button>
      </div>
    </div>
  );
}